import { forwardRef, ReactNode, ComponentType, ButtonHTMLAttributes } from 'react'

const focusReset =
  "focus:outline-none focus:ring-0 focus:ring-transparent focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent cursor-pointer";

const variants = {
  default:
    `bg-[var(--color-default)] text-[var(--color-foreground)] shadow-sm shadow-[var(--color-default)/20] ${focusReset}`,
  primary:
    `bg-primary hover:bg-primary/80 text-white shadow-lg shadow-[var(--color-primary)/20] ${focusReset}`,
  secondary:
    `bg-[var(--color-secondary)] hover:bg-[var(--color-secondary-foreground)] text-[var(--color-foreground)] border border-[var(--border)] ${focusReset}`,
  quaternary:
    `bg-[var(--color-quaternary)] hover:bg-[var(--sidebar-accent-foreground)] text-[var(--color-foreground)] border border-[var(--border)] ${focusReset}`,
  accent:
    `bg-[var(--accent)] hover:bg-[var(--accent-foreground)] text-[var(--color-foreground)] ${focusReset}`,
  success:
    `bg-[var(--color-success)] hover:bg-[var(--color-success)] text-white shadow-lg shadow-[var(--color-success)/20] ${focusReset}`,
  danger:
    `bg-[var(--color-danger)] hover:bg-[var(--color-danger)] text-white shadow-lg shadow-[var(--color-danger)/20] ${focusReset}`,
  warning:
    `bg-amber-500 text-white shadow-lg shadow-amber-500/20 ${focusReset}`,
  blur:
    `bg-white/8 text-[var(--color-foreground)] backdrop-blur-[20px] ${focusReset}`,
  white:
    `bg-white text-black/90 backdrop-blur-[20px] ${focusReset}`,
} as const;

const sizes = {
  sm: 'px-3 py-1.5 text-[12px]',
  md: 'px-3 py-1.5 md:px-5 md:py-2.5 text-[12px]',
  lg: 'px-3 py-1.5 lg:px-6 lgpy-3 text-base',
} as const

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode
  variant?: keyof typeof variants
  size?: keyof typeof sizes
  className?: string
  disabled?: boolean
  loading?: boolean
  icon?: ComponentType<{ className?: string }>
  iconPosition?: 'left' | 'right'
  fullWidth?: boolean
  type?: 'button' | 'submit' | 'reset'
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  disabled = false,
  loading = false,
  icon: Icon,
  iconPosition = 'left',
  fullWidth = false,
  type = 'button',
  ...props
}, ref) => {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={`
        inline-flex items-center justify-center gap-2 
        font-semibold rounded-lg 
        transition-all duration-200 
        disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {loading ? (
        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      ) : (
        <>
          {iconPosition === 'left' && Icon ? <Icon className="w-4 h-4" /> : null}
          {children}
          {iconPosition === 'right' && Icon ? <Icon className="w-4 h-4" /> : null}
        </>
      )}
    </button>
  )
})

Button.displayName = 'Button'

export default Button

